import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Search, Clock, User, TrendingUp, BookOpen, Award, Shield, Filter, Share2, Facebook, Twitter, Linkedin, Mail, Copy, Check } from 'lucide-react';
import { fetchArticles, fetchFeaturedArticles, fetchCategories } from '../../services/blogService';

export default function EnhancedBlogSystem() {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [copiedArticleId, setCopiedArticleId] = useState(null);

  // Category configurations with gradient styling
  const categoryConfig = {
    'Bail Process': { 
      gradient: 'from-blue-500 to-blue-600',
      icon: BookOpen,
      description: 'Understanding bail procedures and requirements'
    },
    'Legal Rights': { 
      gradient: 'from-purple-500 to-purple-600',
      icon: Shield,
      description: 'Know your rights during the bail process'
    },
    'Family Support': { 
      gradient: 'from-teal-500 to-teal-600',
      icon: User,
      description: 'Resources and guidance for families'
    },
    'Sacramento News': { 
      gradient: 'from-indigo-500 to-indigo-600',
      icon: TrendingUp,
      description: 'Local updates and legal changes'
    }
  };

  // Load initial blog data
  useEffect(() => {
    loadBlogData();
  }, []);

  // Load filtered articles when filters change
  useEffect(() => {
    loadArticles();
  }, [selectedCategory, searchQuery, dateRange]);

  const loadBlogData = async () => {
    try {
      setLoading(true);
      
      const [articlesResult, featuredResult, categoriesResult] = await Promise.all([
        fetchArticles(),
        fetchFeaturedArticles(3),
        fetchCategories()
      ]);

      if (articlesResult?.success) {
        setArticles(articlesResult?.articles);
      } else {
        setError(articlesResult?.error);
      }

      if (featuredResult?.success) {
        setFeaturedArticles(featuredResult?.articles);
      }

      if (categoriesResult?.success) {
        setCategories(categoriesResult?.categories);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading blog data:', err);
      setError('Unable to load blog content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async () => {
    try {
      const filters = {
        category: selectedCategory !== 'all' ? selectedCategory : null,
        search: searchQuery || null
      };

      const result = await fetchArticles(filters);

      if (result?.success) {
        let filteredArticles = result?.articles;

        // Apply date range filter
        if (dateRange !== 'all') {
          const now = new Date();
          const filterDate = new Date();
          
          switch(dateRange) {
            case 'week':
              filterDate?.setDate(now?.getDate() - 7);
              break;
            case 'month':
              filterDate?.setMonth(now?.getMonth() - 1);
              break;
            case 'year':
              filterDate?.setFullYear(now?.getFullYear() - 1);
              break;
          }

          filteredArticles = filteredArticles?.filter(article => 
            new Date(article.publishedDate) >= filterDate
          );
        }

        setArticles(filteredArticles);
      }
    } catch (err) {
      console.error('Error loading articles:', err);
    }
  };

  const handleSearch = useCallback((e) => {
    setSearchQuery(e?.target?.value);
  }, []);

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range);
  }, []);

  const handleShare = useCallback((platform, article) => {
    const url = encodeURIComponent(window.location?.origin + '/blog/' + article?.slug);
    const text = encodeURIComponent(article?.title);

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${text}`,
      email: `mailto:?subject=${text}&body=${url}`
    };

    if (shareUrls?.[platform]) {
      window.open(shareUrls?.[platform], '_blank', 'width=600,height=400');
    }
  }, []);

  const copyToClipboard = useCallback(async (article) => {
    try {
      await navigator.clipboard?.writeText(window.location?.origin + '/blog/' + article?.slug);
      setCopiedArticleId(article?.id);
      setTimeout(() => setCopiedArticleId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-blue-200 animate-pulse"></div>
            <div className="absolute inset-0 rounded-full border-t-4 border-blue-600 animate-spin"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading educational resources...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Enhanced Blog System - Educational Resources | Sailors Bail Bonds</title>
        <meta 
          name="description" 
          content="Comprehensive educational resources about bail bonds, legal rights, family support, and Sacramento legal news. Expert insights from licensed bail professionals." 
        />
        <meta name="keywords" content="bail bonds education, legal rights guide, family support resources, Sacramento bail news, bail process help" />
        <link rel="canonical" href="https://sailorsbail.com/enhanced-blog-system" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        {/* Hero Section with 3D Anchor Logo */}
        <section className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-teal-600 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          }}></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              {/* 3D Anchor Logo with Animation */}
              <div className="mb-8 flex justify-center">
                <div className="relative group">
                  <img 
                    src="/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764521073501.png"
                    alt="Animated Sailors Bail Bonds logo with nautical anchor"
                    className="w-32 h-32 object-contain transform transition-transform duration-700 group-hover:scale-110 group-hover:rotate-3"
                    style={{
                      filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))',
                      animation: 'gentleSway 3s ease-in-out infinite'
                    }}
                  />
                  <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                </div>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold font-serif mb-6 leading-tight">
                Knowledge Is Your Anchor
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
                Navigate the bail process with confidence through our comprehensive educational hub. 
                We're here to guide you with clarity, compassion, and expert insights.
              </p>
              
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base">
                <div className="flex items-center bg-white bg-opacity-20 px-6 py-3 rounded-full backdrop-blur-sm">
                  <Award className="w-6 h-6 mr-3" aria-hidden="true" />
                  <span className="font-medium">Expert Insights</span>
                </div>
                <div className="flex items-center bg-white bg-opacity-20 px-6 py-3 rounded-full backdrop-blur-sm">
                  <Shield className="w-6 h-6 mr-3" aria-hidden="true" />
                  <span className="font-medium">Legal Guidance</span>
                </div>
                <div className="flex items-center bg-white bg-opacity-20 px-6 py-3 rounded-full backdrop-blur-sm">
                  <TrendingUp className="w-6 h-6 mr-3" aria-hidden="true" />
                  <span className="font-medium">Updated Regularly</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Advanced Search and Filter Section */}
        <section className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-40">
          <div className="container mx-auto px-4 py-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search Bar */}
                <div className="flex-1 relative">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" aria-hidden="true" />
                    <input
                      type="search"
                      placeholder="Search articles, topics, or keywords..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      aria-label="Search blog articles"
                    />
                  </div>
                </div>

                {/* Filter Toggle Button */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-teal-700 transition-all"
                  aria-expanded={showFilters}
                  aria-label="Toggle filters"
                >
                  <Filter className="w-5 h-5" aria-hidden="true" />
                  Filters
                </button>
              </div>

              {/* Filters */}
              <div className={`mt-4 grid md:grid-cols-2 gap-4 ${showFilters ? 'block' : 'hidden lg:grid'}`}>
                {/* Category Filter */}
                <div>
                  <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    id="category-select"
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e?.target?.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                  >
                    <option value="all">All Categories</option>
                    {categories?.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Range Filter */}
                <div>
                  <label htmlFor="date-range-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Published
                  </label>
                  <select
                    id="date-range-select"
                    value={dateRange}
                    onChange={(e) => handleDateRangeChange(e?.target?.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-white"
                  >
                    <option value="all">All Time</option>
                    <option value="week">Past Week</option>
                    <option value="month">Past Month</option>
                    <option value="year">Past Year</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Cards */}
        <section className="py-12 bg-gradient-to-b from-white to-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold font-serif mb-8 text-gray-900 text-center">
                Explore By Topic
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {Object.entries(categoryConfig)?.map(([name, config]) => {
                  const IconComponent = config?.icon;
                  const isActive = selectedCategory === name;
                  
                  return (
                    <button
                      key={name}
                      onClick={() => handleCategoryChange(name)}
                      className={`group relative p-6 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl ${
                        isActive 
                          ? `bg-gradient-to-br ${config?.gradient} text-white shadow-xl scale-105` 
                          : 'bg-white hover:bg-gradient-to-br hover:' + config?.gradient + ' hover:text-white shadow-md'
                      }`}
                      aria-pressed={isActive}
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                          isActive 
                            ? 'bg-white bg-opacity-20' :'bg-gradient-to-br ' + config?.gradient + ' text-white group-hover:bg-white group-hover:bg-opacity-20'
                        }`}>
                          <IconComponent className="w-8 h-8" aria-hidden="true" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">{name}</h3>
                        <p className={`text-sm ${isActive ? 'text-white text-opacity-90' : 'text-gray-600 group-hover:text-white group-hover:text-opacity-90'}`}>
                          {config?.description}
                        </p>
                      </div>
                      {isActive && (
                        <div className="absolute inset-0 rounded-2xl ring-4 ring-blue-400 ring-opacity-50"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        {featuredArticles?.length > 0 && (
          <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-10">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold font-serif text-gray-900 mb-2">
                      Featured Articles
                    </h2>
                    <p className="text-gray-600">Hand-picked insights to help you navigate bail with confidence</p>
                  </div>
                  <Award className="w-12 h-12 text-blue-600" aria-hidden="true" />
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {featuredArticles?.map((article) => {
                    const categoryGradient = categoryConfig?.[article?.category]?.gradient || 'from-gray-500 to-gray-600';
                    
                    return (
                      <article 
                        key={article?.id}
                        className="group relative bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                      >
                        {/* Featured Badge */}
                        <div className={`absolute top-4 right-4 z-10 bg-gradient-to-r ${categoryGradient} text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg flex items-center gap-2`}>
                          <Award className="w-4 h-4" aria-hidden="true" />
                          Featured
                        </div>
                        {/* Gradient Header */}
                        <div className={`h-48 bg-gradient-to-br ${categoryGradient} relative overflow-hidden`}>
                          <div className="absolute inset-0 bg-black opacity-20"></div>
                          <div className="absolute bottom-4 left-4 right-4">
                            <span className="inline-block bg-white text-gray-900 px-3 py-1 rounded-full text-xs font-bold mb-2">
                              {article?.category}
                            </span>
                          </div>
                        </div>
                        {/* Content */}
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                            {article?.title}
                          </h3>
                          
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                            {article?.excerpt}
                          </p>

                          {/* Meta Information */}
                          <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" aria-hidden="true" />
                              <span>{article?.readingTimeMinutes || 5} min read</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" aria-hidden="true" />
                              <span>{article?.authorName}</span>
                            </div>
                          </div>

                          {/* Author Info */}
                          {article?.authorBio && (
                            <div className="border-t border-gray-200 pt-4 mb-4">
                              <div className="flex items-start gap-3">
                                {article?.authorPhotoUrl ? (
                                  <img 
                                    src={article?.authorPhotoUrl} 
                                    alt={`${article?.authorName} - Author`}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center text-white font-bold">
                                    {article?.authorName?.charAt(0)}
                                  </div>
                                )}
                                <div className="flex-1">
                                  <p className="text-xs text-gray-600 line-clamp-2">{article?.authorBio}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => window.location.href = `/blog/${article?.slug}`}
                              className={`flex-1 bg-gradient-to-r ${categoryGradient} text-white px-4 py-2 rounded-xl font-medium hover:shadow-lg transition-all`}
                            >
                              Read More
                            </button>
                            
                            {/* Share Dropdown */}
                            <div className="relative group/share">
                              <button
                                className="p-2 border-2 border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                                aria-label="Share article"
                              >
                                <Share2 className="w-5 h-5 text-gray-600" aria-hidden="true" />
                              </button>
                              
                              <div className="absolute right-0 top-12 bg-white rounded-xl shadow-2xl border border-gray-200 p-2 opacity-0 invisible group-hover/share:opacity-100 group-hover/share:visible transition-all z-50 min-w-[160px]">
                                <button
                                  onClick={() => handleShare('facebook', article)}
                                  className="w-full flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors text-left"
                                >
                                  <Facebook className="w-4 h-4 text-blue-600" aria-hidden="true" />
                                  <span className="text-sm">Facebook</span>
                                </button>
                                <button
                                  onClick={() => handleShare('twitter', article)}
                                  className="w-full flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors text-left"
                                >
                                  <Twitter className="w-4 h-4 text-blue-400" aria-hidden="true" />
                                  <span className="text-sm">Twitter</span>
                                </button>
                                <button
                                  onClick={() => handleShare('linkedin', article)}
                                  className="w-full flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors text-left"
                                >
                                  <Linkedin className="w-4 h-4 text-blue-700" aria-hidden="true" />
                                  <span className="text-sm">LinkedIn</span>
                                </button>
                                <button
                                  onClick={() => handleShare('email', article)}
                                  className="w-full flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors text-left"
                                >
                                  <Mail className="w-4 h-4 text-gray-600" aria-hidden="true" />
                                  <span className="text-sm">Email</span>
                                </button>
                                <button
                                  onClick={() => copyToClipboard(article)}
                                  className="w-full flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors text-left"
                                >
                                  {copiedArticleId === article?.id ? (
                                    <Check className="w-4 h-4 text-green-600" aria-hidden="true" />
                                  ) : (
                                    <Copy className="w-4 h-4 text-gray-600" aria-hidden="true" />
                                  )}
                                  <span className="text-sm">
                                    {copiedArticleId === article?.id ? 'Copied!' : 'Copy Link'}
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* All Articles */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold font-serif text-gray-900 mb-2">
                    {selectedCategory !== 'all' ? `${selectedCategory} Articles` : 'All Articles'}
                  </h2>
                  <p className="text-gray-600">
                    {articles?.length} article{articles?.length !== 1 ? 's' : ''} found
                  </p>
                </div>
                <BookOpen className="w-12 h-12 text-blue-600" aria-hidden="true" />
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-8" role="alert">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">!</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-red-900 font-bold mb-1">Unable to Load Content</h3>
                      <p className="text-red-800">{error}</p>
                      <button
                        onClick={loadBlogData}
                        className="mt-3 text-red-700 font-medium hover:underline"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {articles?.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
                  <BookOpen className="w-20 h-20 text-gray-400 mx-auto mb-6" aria-hidden="true" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No Articles Found</h3>
                  <p className="text-gray-600 text-lg mb-6 max-w-md mx-auto">
                    {searchQuery || selectedCategory !== 'all' ?'We couldn\'t find any articles matching your criteria. Try adjusting your filters or search terms.' :'Our educational resources are being prepared. Check back soon for helpful insights!'}
                  </p>
                  {(searchQuery || selectedCategory !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('all');
                        setDateRange('all');
                      }}
                      className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-8 py-3 rounded-xl font-medium hover:shadow-lg transition-all"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {articles?.map((article) => {
                    const categoryGradient = categoryConfig?.[article?.category]?.gradient || 'from-gray-500 to-gray-600';
                    
                    return (
                      <article 
                        key={article?.id}
                        className="group bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                      >
                        {/* Gradient Header */}
                        <div className={`h-40 bg-gradient-to-br ${categoryGradient} relative overflow-hidden`}>
                          <div className="absolute inset-0 bg-black opacity-20"></div>
                          <div className="absolute bottom-3 left-3">
                            <span className="inline-block bg-white text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
                              {article?.category}
                            </span>
                          </div>
                        </div>
                        {/* Content */}
                        <div className="p-5">
                          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                            {article?.title}
                          </h3>
                          
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                            {article?.excerpt}
                          </p>

                          {/* Meta Information */}
                          <div className="flex items-center gap-3 text-xs text-gray-500 mb-4 pb-4 border-b border-gray-200">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" aria-hidden="true" />
                              <span>{article?.readingTimeMinutes || 5} min</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" aria-hidden="true" />
                              <span className="truncate">{article?.authorName}</span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => window.location.href = `/blog/${article?.slug}`}
                              className={`flex-1 bg-gradient-to-r ${categoryGradient} text-white px-4 py-2 rounded-xl text-sm font-medium hover:shadow-lg transition-all`}
                            >
                              Read Article
                            </button>
                            
                            <button
                              onClick={() => copyToClipboard(article)}
                              className="p-2 border-2 border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                              aria-label="Copy article link"
                            >
                              {copiedArticleId === article?.id ? (
                                <Check className="w-4 h-4 text-green-600" aria-hidden="true" />
                              ) : (
                                <Share2 className="w-4 h-4 text-gray-600" aria-hidden="true" />
                              )}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Emergency Contact Banner with Crisis Focus */}
        <section className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)'
          }}></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block bg-white text-red-600 px-4 py-2 rounded-full font-bold text-sm mb-4 animate-pulse">
                24/7 Emergency Support
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Need Immediate Bail Assistance?
              </h3>
              <p className="text-red-100 text-lg mb-6 max-w-2xl mx-auto">
                We understand this is a challenging time. Our compassionate team is standing by around the clock 
                to guide you through the bail process with clarity and care.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a 
                  href="tel:+19162563468"
                  className="inline-flex items-center bg-white text-red-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-red-50 transition-all shadow-2xl hover:shadow-3xl transform hover:scale-105"
                >
                  <span className="mr-2">📞</span> Call (916) 256-3468
                </a>
                <a
                  href="/contact-us"
                  className="inline-flex items-center border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-red-600 transition-all"
                >
                  Contact Us Online
                </a>
              </div>
              <p className="text-red-100 text-sm mt-6">
                <Shield className="w-4 h-4 inline mr-1" aria-hidden="true" />
                Licensed & Professional • Serving Sacramento, Stockton & Modesto
              </p>
            </div>
          </div>
        </section>

        {/* Trust & Legal Disclaimer */}
        <section className="bg-gray-50 py-8 border-t border-gray-200">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-gray-600 text-sm leading-relaxed">
                <strong className="text-gray-900">Legal Disclaimer:</strong> The information provided in our blog is for educational purposes only 
                and does not constitute legal advice. Every situation is unique. We recommend consulting with a legal professional 
                for advice specific to your circumstances. Our licensed bail agents are here to help guide you through the process 
                with compassion and expertise.
              </p>
            </div>
          </div>
        </section>
      </div>
      {/* CSS for Logo Animation */}
      <style jsx>{`
        @keyframes gentleSway {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(3deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
}